import { tns } from 'tiny-slider/src/tiny-slider';
import ModalDefault from './modal-default';
import EqualHeight from '../util/equal-height';

class FilmSlider {
	constructor(el, loadingSpinner) {
		this.el = el;
		this.sectionBlock = this.el.closest('section');
		this.loadingSpinner = loadingSpinner;
		this.slidesPerView = parseInt(this.el.dataset.slidesPerView, 10)
			? parseInt(this.el.dataset.slidesPerView, 10)
			: 3;
		this.showing = this.el.dataset.showing ? this.el.dataset.showing : null;
		this.topic = this.el.dataset.topic ? this.el.dataset.topic : null;
		this.dataSource = this.el.dataset.source ? this.el.dataset.source : null;
		this.displayType = this.el.dataset.displayType ? this.el.dataset.displayType : null;

		this.instances = {
			modals: [],
			equalHeight: [],
			slider: null
		};

		this.makeSliderButtons();
		this.generateSlides();
		this.buildManualSlider();
	}

	buildSlider = () => {
		const filmSlider = tns({
			container: this.el,
			responsive: {
				350: {
					items: 1
				},
				500: {
					items: 2
				},
				900: {
					items: this.slidesPerView
				}
			},
			autoplay: true,
			autoplayButtonOutput: false,
			// mouseDrag: true,
			preventScrollOnTouch: 'auto',
			swipeAngle: false,
			speed: 400,
			controls: true,
			prevButton: this.prevButton,
			nextButton: this.nextButton,
			gutter: 30,
			nav: false
		});

		this.instances.slider = filmSlider;

		// Update tabindex initially
		this.updateTabIndexForHiddenSlides();

		// Add event listener for index changes
		if(this.instances.slider) {
			this.instances.slider.events.on('indexChanged', () => {
				this.updateTabIndexForHiddenSlides();
			});
		}

		this.sliderControls();

		return filmSlider;
	};

	updateTabIndexForHiddenSlides = () => {
		const slides = this.el.querySelectorAll('[aria-hidden="true"]');
		slides.forEach((slide) => {
			slide
				.querySelectorAll('.watch-button, .button, .component-film-slider__text-wrapper')
				.forEach((element) => {
					element.setAttribute('tabindex', '-1');
				});
			slide
				.querySelectorAll('.component-film-slider__card-poster__link')
				.forEach((element) => {
					element.setAttribute('tabindex', '-1');
				});
		});

		const visibleSlides = Array.from(this.el.children).filter(
			(slide) => !slide.hasAttribute('aria-hidden')
		);
		visibleSlides.forEach((slide) => {
			slide
				.querySelectorAll('.watch-button, .button, .component-film-slider__text-wrapper')
				.forEach((element) => {
					element.removeAttribute('tabindex');
				});
			slide
				.querySelectorAll('.component-film-slider__card-poster__link')
				.forEach((element) => {
					element.removeAttribute('tabindex');
				});
		});
	};

	buildManualSlider = () => {
		if (this.dataSource === 'manual') {
			this.buildSlider();
			this.generateModals();
			this.equalizeHeights();
			this.loadingSpinner.style.display = 'none';
		}
	};

	fetchSlides = async () => {
		try {
	
			let endpoint = '/wp-json/wp/v2/film-meta?show_all';
	
			if (window.location.pathname.includes('independentlens')) {
				endpoint = '/independentlens/wp-json/wp/v2/film-meta?show_all';
			}
			// Update endpoint for staging site
			if (window.location.pathname.includes('independentlens')) {
				endpoint = '/independentlens/wp-json/wp/v2/film-meta?show_all';
			}
	
			const queryParams = [];
			if (this.showing) queryParams.push(`showing=${this.showing}`);
			if (this.topic) queryParams.push(`topic=${this.topic}`);
			if (queryParams.length > 0) endpoint += '&' + queryParams.join('&');

			let response = await fetch(endpoint);
			let data = await response.json();
	
			if (document.querySelector('.block-film-slider') && (!data?.films?.length) && this.showing === 'coming-soon') {
				const title = this.sectionBlock?.querySelector('.block-film-slider__title');
				if (title) title.textContent = 'Streaming Now';
	
				response = await fetch('/wp-json/wp/v2/film-meta?show_all&showing=streaming-now');
				data = await response.json();
			}

			if (this.showing === 'coming-soon' && (!data?.films?.length)) {
				const sliderWrapper = this.sectionBlock?.querySelector('.tmpl-single-film__film-slider-coming-soon');
				if (sliderWrapper) sliderWrapper.style.display = 'none';
			}

			return data;
		} catch (error) {
			return { films: [] };
		}
	};

	generateSlides = async () => {
		if (this.dataSource === 'taxonomy') {
			const data = await this.fetchSlides();
			const slides = data.films.slice(0, 30);

			if (slides.length > 0) {
				let slideMarkup = '';
				slides.forEach((slide) => {
					if (this.displayType === 'cards') {
						slideMarkup += slide.slider_card_markup;
					} else {
						slideMarkup += slide.slider_poster_markup;
					}
				});
				this.el.innerHTML += slideMarkup;
			}
			this.buildSlider();
			this.generateModals();
			this.equalizeHeights();
			this.loadingSpinner.style.display = 'none';
		}
	};

	sliderControls = () => {
		this.el.addEventListener('mouseenter', () => {
			this.instances.slider.pause();
		});

		this.el.addEventListener('mouseleave', () => {
			this.instances.slider.play();
		});

		document.addEventListener('keyup', (e) => {
			e.preventDefault();
			if (e.key === 'ArrowRight') {
				this.instances.slider.goTo('next');
			}
			if (e.key === 'ArrowLeft') {
				this.instances.slider.goTo('prev');
			}
		});
	};

	makeSliderButtons = () => {
		// Create custom buttons dynamically
		const prevButton = document.createElement('button');
		prevButton.innerHTML = `
		<div class="slider-icon-container">
			<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
				<path d="M15 18l-6-6 6-6"></path>
			</svg>
    </div>
  `;
		prevButton.setAttribute('aria-label', 'Previous Slide');
		prevButton.className = 'custom-prev';

		const nextButton = document.createElement('button');
		nextButton.innerHTML = `
		<div class="slider-icon-container">
			<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
				<path d="M9 18l6-6-6-6"></path>
			</svg>
    </div>
  `;
		nextButton.setAttribute('aria-label', 'Next Slide');
		nextButton.className = 'custom-next';

		this.prevButton = prevButton;
		this.nextButton = nextButton;

		// Append buttons to the parent of the slider container
		this.el.parentElement.appendChild(prevButton);
		this.el.parentElement.appendChild(nextButton);
	};

	generateModals = () => {
		Array.from(this.el.querySelectorAll('.js__modal--default')).forEach((modalDefaultEl) => {
			this.instances.modals.push(new ModalDefault(modalDefaultEl));
		});
	};

	equalizeHeights = () => {
		this.instances.equalHeight.push(new EqualHeight(this.el));
	};
}

export default FilmSlider;
